import { criterion, fail, fromCriteria } from "@/core/check";
import type { Lesson } from "@/core/types";
import { colorFamily } from "@/office/colors";
import {
  countWords,
  hasHighlight,
  isUnderlined,
  readDocument,
  usedFontNames,
} from "@/office/word/document";

export const lektionAbschlussuebung: Lesson = {
  id: "abschlussuebung",
  title: "Abschlussübung: Eine saubere Kurznotiz",
  summary: "Alles aus dem Modul in einem kleinen, vollständig formatierten Dokument anwenden.",
  durationMinutes: 12,
  learningGoals: [
    "Schriftart, Größe, Auszeichnung und Farbe kombiniert einsetzen",
    "Ein Dokument mit erkennbarer Gliederung aufbauen",
    "Typografische Faustregeln selbstständig einhalten",
  ],
  steps: [
    {
      id: "kurznotiz",
      title: "Die Kurznotiz aufbauen",
      explanation: [
        "Jetzt bringst du alles zusammen. Lege am besten ein neues, leeres Dokument an " +
          "(Strg + N) und schreibe eine kurze Notiz an Kolleginnen und Kollegen – zum " +
          "Beispiel eine Einladung zu einer Besprechung.",
        "Die Notiz braucht eine Titelzeile und mindestens drei Absätze Fließtext. " +
          "Formatiere sie anschließend nach den Regeln, die du in diesem Modul gelernt hast.",
      ],
      keyPoints: [
        "Titel deutlich größer und fett – aber nicht unterstrichen.",
        "Fließtext bei 10 bis 12 pt halten.",
        "Höchstens zwei Schriftarten im gesamten Dokument.",
      ],
      task: {
        instruction:
          "Erstelle eine Notiz mit Titelzeile und mindestens drei Fließtextabsätzen. Der " +
          "Titel ist mindestens 16 pt groß und fett, der Fließtext 10 bis 12 pt. Hebe " +
          "genau eine wichtige Stelle im Fließtext hervor (fett, farbig oder mit dem " +
          "Textmarker) und verwende insgesamt höchstens zwei Schriftarten.",
        hint: "Arbeite die Kriterien von oben nach unten ab und klicke zwischendurch immer wieder auf \"Prüfen\" – die Liste zeigt dir, was noch fehlt.",
        check: async () => {
          const snapshot = await readDocument();
          if (countWords(snapshot) < 20) {
            return fail(
              "Für die Abschlussübung braucht es etwas mehr Text (mindestens 20 Wörter).",
              "Schreibe zuerst den kompletten Inhalt und formatiere ihn danach.",
            );
          }

          const [title, ...rest] = snapshot.filledParagraphs;
          const bodyParagraphs = rest;

          const titleIsLarge = title.runs.every(
            (run) => typeof run.size === "number" && run.size >= 16,
          );
          const titleIsBold = title.runs.every((run) => run.bold === true);
          const titleNotUnderlined = title.runs.every((run) => !isUnderlined(run));

          const bodySizeOk =
            bodyParagraphs.length > 0 &&
            bodyParagraphs.every((paragraph) =>
              paragraph.runs.every(
                (run) => typeof run.size === "number" && run.size >= 10 && run.size <= 12,
              ),
            );

          const bodyRuns = bodyParagraphs.flatMap((paragraph) => paragraph.runs);
          const emphasized = bodyRuns.filter(
            (run) =>
              run.bold === true ||
              hasHighlight(run) ||
              (colorFamily(run.color) !== null &&
                colorFamily(run.color) !== "schwarz" &&
                colorFamily(run.color) !== "grau"),
          );

          const fontNames = usedFontNames(snapshot);

          return fromCriteria(
            [
              criterion("Mindestens drei Fließtextabsätze unter dem Titel", bodyParagraphs.length >= 3),
              criterion("Titelzeile ist mindestens 16 pt groß", titleIsLarge),
              criterion("Titelzeile ist fett", titleIsBold),
              criterion("Titelzeile ist nicht unterstrichen", titleNotUnderlined),
              criterion("Fließtext liegt bei 10 bis 12 pt", bodySizeOk),
              criterion(
                "Genau eine Stelle im Fließtext ist hervorgehoben",
                emphasized.length >= 1 && emphasized.length <= 4,
              ),
              criterion(
                `Höchstens zwei Schriftarten im Dokument (aktuell: ${fontNames.length})`,
                fontNames.length > 0 && fontNames.length <= 2,
              ),
            ],
            {
              success:
                "Stark – deine Notiz erfüllt alle typografischen Grundregeln aus diesem Modul.",
              failure: "Fast geschafft. Diese Punkte fehlen noch:",
              hint: "Der erste Absatz mit Inhalt gilt als Titelzeile. Achte darauf, dass wirklich die gesamte Zeile fett und groß ist.",
            },
          );
        },
      },
    },
    {
      id: "rueckblick",
      title: "Rückblick",
      explanation: [
        "Du kennst jetzt die vier Stellschrauben der Zeichenformatierung: Schriftart, " +
          "Schriftgröße, Auszeichnung und Farbe. Damit lässt sich jedes Word-Dokument " +
          "lesbar und aufgeräumt gestalten.",
        "Im nächsten Modul geht es um Formatvorlagen. Dort weist du diese vier " +
          "Eigenschaften nicht mehr einzeln per Hand zu, sondern speicherst sie als " +
          "wiederverwendbare Vorlage – die Grundlage für automatische " +
          "Inhaltsverzeichnisse und einheitliche Dokumente.",
      ],
      keyPoints: [
        "Schriftart: maximal zwei pro Dokument.",
        "Schriftgröße: Fließtext 10–12 pt, deutlicher Sprung zur Überschrift.",
        "Auszeichnung: sparsam und nie gestapelt.",
        "Farbe: eine Akzentfarbe, immer mit ausreichend Kontrast.",
      ],
    },
  ],
};
